import { z } from "zod";

export const MAX_WORKSPACE_PERSISTENCE_BYTES = 1024 * 1024;
const id = z.string().min(1).max(256).refine(value => value.trim() === value);
const fingerprint = z.string().regex(/^[a-f0-9]{64}$/);
const reference = z.object({ id }).passthrough();
const references = z.array(reference).max(1000).refine(refs => new Set(refs.map(ref => ref.id)).size === refs.length);
export const workspaceLayoutSchema = z.object({
  componentsMap: references,
  notificationWindowDismissed: z.boolean().optional(),
  lastLayoutMode: z.string().max(64).optional(),
}).strict();
// Old preference records may still hold JSON-encoded references. Keep their
// original fingerprint while the client prepares a normalized replacement.
const storedLayoutSchema = workspaceLayoutSchema.extend({ componentsMap: z.union([references, z.string().max(64 * 1024)]) });
const component = z.object({ id }).passthrough();
const changes = z.array(z.object({ componentId: id, expectedFingerprint: fingerprint.nullable(), payload: component }).strict())
  .max(100).refine(values => new Set(values.map(value => value.componentId)).size === values.length
    && values.every(value => value.componentId === value.payload.id));
const owner = { expectedOwnerId: id, expectedCompanyId: id };
export const workspaceRecoverySaveGuardSchema = z.object({ requestId: id.pipe(z.string().max(128)),
  expectedCatalogRevision: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER), expectedSceneId: id.nullable(),
}).strict();
export type WorkspaceRecoverySaveGuard = z.infer<typeof workspaceRecoverySaveGuardSchema>;
export const workspacePersistenceRequestSchema = z.discriminatedUnion("mode", [
  z.object({ ...owner, mode: z.literal("inspect"), componentIds: z.array(id).max(100).refine(ids => new Set(ids).size === ids.length) }).strict(),
  z.object({ ...owner, mode: z.literal("save"), changes, layout: z.object({ expectedFingerprint: fingerprint, payload: workspaceLayoutSchema }).strict().optional(),
    recovery: workspaceRecoverySaveGuardSchema.optional() }).strict(),
]).refine(input => input.mode === "inspect" || (input.changes.length > 0 || input.layout !== undefined) && (!input.recovery || input.layout !== undefined));
export type WorkspacePersistenceRequest = z.infer<typeof workspacePersistenceRequestSchema>;
export const workspaceSnapshotSchema = z.object({
  ownerId: id,
  components: z.array(z.object({ componentId: id, fingerprint: fingerprint.nullable(), payload: component.nullable() }).strict()).max(100),
  layout: z.object({ fingerprint, payload: storedLayoutSchema }).strict(),
}).strict();
export const workspaceSaveResultSchema = z.discriminatedUnion("outcome", [
  z.object({ outcome: z.literal("conflict"), componentId: id.nullable() }).strict(),
  z.object({ outcome: z.enum(["saved", "unchanged"]), ownerId: id,
    components: z.array(z.object({ componentId: id, fingerprint }).strict()).max(100), layoutFingerprint: fingerprint.nullable(),
  }).strict(),
]);

/** The server and client use the same request-to-receipt consistency checks. */
export function parseWorkspacePersistenceResult(input: WorkspacePersistenceRequest, value: unknown) {
  const invalidReply = () => new Error("Workspace storage reply could not be verified");
  if (input.mode === "inspect") {
    const result = workspaceSnapshotSchema.parse(value);
    if (result.ownerId !== input.expectedOwnerId || result.components.length !== input.componentIds.length
      || result.components.some((component, index) => component.componentId !== input.componentIds[index]
        || (component.fingerprint === null) !== (component.payload === null)
        || component.payload !== null && component.payload.id !== component.componentId)) throw invalidReply();
    return result;
  }
  const result = workspaceSaveResultSchema.parse(value);
  if (result.outcome === "conflict") {
    if (result.componentId === null ? !input.layout : !input.changes.some(change => change.componentId === result.componentId)) throw invalidReply();
    return result;
  }
  if (result.ownerId !== input.expectedOwnerId || result.components.length !== input.changes.length
    || result.components.some((component, index) => component.componentId !== input.changes[index].componentId)
    || (result.layoutFingerprint !== null) !== Boolean(input.layout)) throw invalidReply();
  return result;
}
export type WorkspacePersistenceResult = ReturnType<typeof parseWorkspacePersistenceResult>;
